import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {BehaviorSubject, combineLatest, from} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {isNotNullOrUndefined} from '../../helper/helper';
import {EventEntity, EventEntityFromGRPCBackend} from '../entities/event.entity';
import {State} from '../State';
import {AppGatewayService} from './app-gateway.service';
import {EventFilterObject, GetEventRequest} from '@dave/types/dist/proto/history/event';
import {GatewayResponse} from '@dave/types';
import {Dictionary} from '@ngrx/entity';


enum ErrorCodes {
    Add = 'Event Hinzufügen fehlgeschlagen',
    Load = 'Event Abrufen fehlgeschlagen',
    Modify = 'Event Bearbeiten fehlgeschlagen',
    Remove = 'Event Löschen fehlgeschlagen',
}

@Injectable({
    providedIn: 'root',
})
export class EventDataService {
    protected cache = new BehaviorSubject<Map<number, EventEntity>>(new Map());
    protected fetchedCustomers: number[] = [];
    protected fetchedCommissions: number[] = [];
    protected fetchedPersons: number[] = [];

    constructor(private gatewayService: AppGatewayService) {

    }

    ConvertDict(events: EventEntity[]):Dictionary<EventEntity> {
        let out: Dictionary<EventEntity> = {};
        events.forEach(e => out[e.Id] = e);
        return out;
    }

    FilterEvents(events: EventEntity[]) {
        return events.filter(e => !e.IsTask);
    }

    FilterTasks(events: EventEntity[]) {
        return events.filter(e => e.IsTask && !e.AdditionalData?.IsDailyToDo);
    }

    FilterDailyToDo(events: EventEntity[]) {
        return events.filter(e => e.IsTask && !!e.AdditionalData?.IsDailyToDo)
    }


    AddEvent(event: EventEntity) {
        let lv = this.cache.value;
        lv.set(event.Id, event);
        this.cache.next(lv);
    }

    UpdateEvent(event: EventEntity) {
        let lv = this.cache.value;
        if (lv.has(event.Id)) {
            lv.set(event.Id, event);
            this.cache.next(lv);
        }
    }

    GetSingleEvent$(eventId: number): Observable<EventEntity> {
        let c = this.cache.value.get(eventId);
        if (c !== undefined) {
            return this.cache.pipe(
                map(e => e.get(eventId)),
                filter(isNotNullOrUndefined)
            );
        } else {
            return combineLatest([
                this.cache.pipe(
                    map(e => e.get(eventId)),
                    filter(isNotNullOrUndefined)
                ),
                this.GetSingleEvent(eventId)
            ]).pipe(
                map(([cached, _]) => cached)
            );
        }
    }

    GetSingleEvent(eventId: number): Promise<EventEntity> {
        return new Promise((resolve, reject) => {
            let c = this.cache.value.get(eventId);
            if (c !== undefined) {
                resolve(c);
            } else {
                this.gatewayService.Request({
                    HistoryGetSingleEvent: {
                        Id: eventId.toString()
                    }
                }).then(res => {
                    if (res.HistoryGetSingleEvent) {
                        let e = EventEntityFromGRPCBackend(res.HistoryGetSingleEvent.Events[0]);
                        let nv = this.cache.value;
                        nv.set(e.Id, e);
                        this.cache.next(nv);
                        resolve(e);
                    } else {
                        reject(new Error('fetch event error'));
                    }
                }).catch(err => {
                    reject(err);
                });
            }
        });
    }

    GetFromCommissionDict$(commissionId: number): Observable<Dictionary<EventEntity>> {
        return this.GetFromCommission$(commissionId).pipe(map(events => this.ConvertDict(events)));
    }

    GetFromCommissionEvents$(commissionId: number): Observable<EventEntity[]> {
        return this.GetFromCommission$(commissionId).pipe(map(events => this.FilterEvents(events)));
    }

    GetFromCommissionTasks$(commissionId: number): Observable<EventEntity[]> {
        return this.GetFromCommission$(commissionId).pipe(map(events => this.FilterTasks(events)));
    }

    GetFromCommissionsTasks$(commissionIds: number[]): Observable<EventEntity[]> {
        // todo: mehrere commissions auf einmal abrufen
        return combineLatest(commissionIds.map(commissionId => this.GetFromCommission$(commissionId))).pipe(map(events => this.FilterTasks(events.flat())));
    }

    GetFromCustomerTasks$(customerId: number): Observable<EventEntity[]> {
        return this.GetFromCustomer$(customerId).pipe(map(events => this.FilterTasks(events)));
    }

    GetFromCustomer$(customerId: number): Observable<EventEntity[]> {
        if (this.fetchedCustomers.includes(customerId)) {
            return this.cache.pipe(map(c => [...c.values()].filter(c => c.CustomerId === customerId)));
        } else {
            return combineLatest([
                this.cache.pipe(map(c => [...c.values()].filter(c => c.CustomerId === customerId))),
                from(new Promise<EventEntity[]>((resolve, reject) => {
                    this.GetFromCustomer(customerId).then(res => {
                        resolve(res);
                    }).catch(err => {
                        reject(err);
                    });
                }))
            ]).pipe(map(([cached, _]) => cached));
        }
    }

    GetFromCustomer(customerId: number): Promise<EventEntity[]> {
        if (this.fetchedCustomers.includes(customerId)) {
            return new Promise<EventEntity[]>((resolve, reject) => {
                resolve([...this.cache.value.values()].filter(c => c.CustomerId === customerId));
            });
        }
        this.fetchedCustomers.push(customerId);
        let req = {
            CustomerId: customerId.toString(),
            AfterId: '0',
            Paging: true,
            PageSize: '5000',
            Filter: {} as EventFilterObject,
        } as GetEventRequest;
        return new Promise((resolve, reject) => {
            let result: EventEntity[] = [];

            let handleResult = function(res: GatewayResponse) {
                let resEvents = res.HistoryGetPagedEvents.PagedEvents.Data;
                result.push(...resEvents.map(e => EventEntityFromGRPCBackend(e)));
                if (res.HistoryGetPagedEvents.PagedEvents.LastPage) {
                    let nv = this.cache.value;
                    result.forEach(e => nv.set(e.Id, e));
                    this.cache.next(nv);
                    resolve(result);
                    return;
                } else {
                    req.AfterId = resEvents[resEvents.length - 1].Id;
                    this.gatewayService.Request({
                        HistoryGetPagedEvents: req
                    }).then(handleResult.bind(this)).catch(reject);
                }
            };

            this.gatewayService.Request({
                HistoryGetPagedEvents: req
            }).then(handleResult.bind(this)).catch(reject);
        });
    }

    GetFromCommission$(commissionId: number): Observable<EventEntity[]> {
        if (this.fetchedCommissions.includes(commissionId)) {
            return this.cache.pipe(map(c => [...c.values()].filter(c => c.CommissionId === commissionId)));
        } else {
            return combineLatest([
                this.cache.pipe(map(c => [...c.values()].filter(c => c.CommissionId === commissionId))),
                from(new Promise<EventEntity[]>((resolve, reject) => {
                    this.GetFromCommission(commissionId).then(res => {
                        resolve(res);
                    }).catch(err => {
                        reject(err);
                    });
                }))
            ]).pipe(map(([cached, _]) => cached));
        }
    }


    GetFromPerson$(personId: number): Observable<EventEntity[]> {
        if (this.fetchedPersons.includes(personId)) {
            return this.cache.pipe(map(c => [...c.values()].filter(c => c.PersonId === personId)));
        } else {
            return combineLatest([
                this.cache.pipe(map(c => [...c.values()].filter(c => c.PersonId === personId))),
                from(new Promise<EventEntity[]>((resolve, reject) => {
                    this.GetFromPerson(personId).then(res => {
                        resolve(res);
                    }).catch(err => {
                        reject(err);
                    });
                }))
            ]).pipe(map(([cached, _]) => cached));
        }
    }

    GetFromCommission(commissionId: number): Promise<EventEntity[]> {
        if (this.fetchedCommissions.includes(commissionId)) {
            return new Promise<EventEntity[]>((resolve, reject) => {
                resolve([...this.cache.value.values()].filter(c => c.CommissionId === commissionId));
            });
        }
        this.fetchedCommissions.push(commissionId);
        let req = {
            AfterId: '0',
            Paging: true,
            PageSize: '5000',
            Filter: {
                CommissionIds: [commissionId.toString()]
            } as EventFilterObject,
        } as GetEventRequest;
        return new Promise((resolve, reject) => {
            let result: EventEntity[] = [];

            let handleResult = function(res: GatewayResponse) {
                let resEvents = res.HistoryGetPagedEvents.PagedEvents.Data;
                result.push(...resEvents.map(e => EventEntityFromGRPCBackend(e)));
                if (res.HistoryGetPagedEvents.PagedEvents.LastPage) {
                    let nv = this.cache.value;
                    result.forEach(e => nv.set(e.Id, e));
                    this.cache.next(nv);
                    resolve(result);
                    return;
                } else {
                    req.AfterId = resEvents[resEvents.length - 1].Id;
                    this.gatewayService.Request({
                        HistoryGetPagedEvents: req
                    }).then(handleResult.bind(this)).catch(reject);
                }
            };

            this.gatewayService.Request({
                HistoryGetPagedEvents: req
            }).then(handleResult.bind(this)).catch(reject);
        });
    }

    GetFromPerson(personId: number): Promise<EventEntity[]> {
        if (this.fetchedPersons.includes(personId)) {
            return new Promise<EventEntity[]>((resolve, reject) => {
                resolve([...this.cache.value.values()].filter(c => c.PersonId === personId));
            });
        }
        let req = {
            AfterId: '0',
            Paging: true,
            PageSize: '5000',
            Filter: {
                Persons: [personId.toString()]
            } as EventFilterObject,
        } as GetEventRequest;
        return new Promise((resolve, reject) => {
            let result: EventEntity[] = [];

            let handleResult = function(res: GatewayResponse) {
                let resEvents = res.HistoryGetPagedEvents.PagedEvents.Data;
                result.push(...resEvents.map(e => EventEntityFromGRPCBackend(e)));
                if (res.HistoryGetPagedEvents.PagedEvents.LastPage) {
                    let nv = this.cache.value;
                    result.forEach(e => nv.set(e.Id, e));
                    this.cache.next(nv);
                    this.fetchedPersons.push(personId);
                    resolve(result);
                    return;
                } else {
                    req.AfterId = resEvents[resEvents.length - 1].Id;
                    this.gatewayService.Request({
                        HistoryGetPagedEvents: req
                    }).then(handleResult.bind(this)).catch(reject);
                }
            };

            this.gatewayService.Request({
                HistoryGetPagedEvents: req
            }).then(handleResult.bind(this)).catch(reject);
        });
    }
}
